"use client";

import {
  Lock,
  Share2,
  Globe,
  Users,
  TrendingUp,
  DollarSign,
  Clock,
  Wrench,
  AlertCircle,
} from "@/components/icons";
import { ProConsColumn, type ProConsItem } from "../_shared/pros-cons-column";

const PROS: ProConsItem[] = [
  {
    label: "Anonymity",
    icon: Lock,
    body: "A proxy replaces your IP with its own for the destinations you configure it for. For light personal use it can serve a similar purpose to a VPN, and is typically faster since there's no tunneling or encryption overhead.",
  },
  {
    label: "Traffic distribution",
    icon: Share2,
    body: "Spread requests across many IPs to scale your scrapers and collect large volumes of public data reliably.",
  },
  {
    label: "Geohopping",
    icon: Globe,
    body: "Access region-specific public content by routing through another country. Most providers offer country and city-level targeting.",
  },
  {
    label: "Parallel sessions",
    icon: Users,
    body: "Run many concurrent scraping sessions from separate IPs to collect public data across regions in parallel without hitting per-IP limits.",
  },
  {
    label: "General scalability",
    icon: TrendingUp,
    body: "Any activity that would otherwise be limited by a single IP (scraping, API testing, web automation) can be expanded almost without bound.",
  },
];

const CONS: ProConsItem[] = [
  {
    label: "Cost",
    icon: DollarSign,
    body: "Quality correlates with price. Dedicated proxies cost more than shared. Residential and mobile cost more than datacenter. Cheap proxies are cheap for a reason.",
  },
  {
    label: "Higher latency",
    icon: Clock,
    body: "Your traffic has to physically route to the proxy before continuing on to its destination. The further the proxy, the more latency it adds.",
  },
  {
    label: "Additional software",
    icon: Wrench,
    body: "Proxies alone aren't enough for most use cases. You'll usually pair them with anti-detect browsers, headless tooling, or scraping frameworks.",
  },
  {
    label: "Bad neighbors",
    icon: AlertCircle,
    body: "Shared proxies mean your IP may have a history. If a bad actor used it before you, the target site might already distrust the address.",
  },
];

export default function ProsCons() {
  return (
    <section
      aria-labelledby="pros-cons-heading"
      className="border-border py-section relative border-b"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-3xl">
          <p className="text-muted-foreground mb-3 text-xs font-semibold">Trade-offs</p>
          <h2
            id="pros-cons-heading"
            className="text-foreground mb-3 scroll-mt-40 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            Pros &amp; cons of using a proxy
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            Proxies unlock scale, but they come with trade-offs worth understanding before you
            commit.
          </p>
        </div>

        <div className="grid gap-2 sm:gap-4 md:grid-cols-2">
          <ProConsColumn heading="Pros" items={PROS} positive />
          <ProConsColumn heading="Cons" items={CONS} positive={false} />
        </div>
      </div>
    </section>
  );
}
